'use client'

import Link from 'next/link'
import React, { FC } from 'react'

export interface NavItemProps {
  icon: React.ReactNode
  label: string
  href: string
  onClick?: () => void
  active?: boolean
  badge?: string
  external?: boolean
  /** `sidebar` is the compact row; `drawer` is the taller mobile one. */
  variant?: 'sidebar' | 'drawer'
}

const VARIANTS = {
  // py-2 + rounded-lg. This was py-1.5/rounded-md, squeezed that tight to keep
  // an eleven-item sidebar off a scrollbar on a laptop — but at ~28px a row the
  // list read as a wall of text rather than a menu, and the active pill was too
  // short to look deliberate. 33px a row costs ~50px over eleven items, which
  // the panel's overflow-y-auto absorbs on a short viewport.
  sidebar: 'px-3 py-2 rounded-lg text-[13px]',
  // Taller and a touch wider: this one is tapped, not clicked.
  drawer: 'px-3 py-3 rounded-lg text-xs',
}

export const NavItem: FC<NavItemProps> = ({
  icon,
  label,
  href,
  onClick,
  active = false,
  badge,
  external = false,
  variant = 'sidebar',
}) => {
  // dashboard-nav-link: an unlayered colour rule in tokens.css. It was added to
  // beat Bootstrap's `a { color }` — Bootstrap is gone now, but these rows are
  // <a>/<Link>, so without it the browser's own link colour applies.
  const className = `dashboard-nav-link flex items-center gap-2.5 cursor-pointer transition-colors leading-tight text-dashboard-text ${
    VARIANTS[variant]
  } ${
    // Weight carries the active state as well as the wash does, and it stops
    // the row you are on from depending on colour alone.
    active
      ? 'bg-dashboard-accent-soft font-semibold'
      : 'font-medium hover:bg-dashboard-accent-soft/50'
  }`

  // The icon takes its colour from the row's state rather than from the nav
  // config, which used to hardcode the accent on every one of them — so a
  // sidebar of eleven destinations was eleven blue icons and nothing stood out.
  // Neutral when idle, accent only on the row you are actually on.
  const content = (
    <>
      {icon && (
        <span
          className={`flex-shrink-0 ${active ? 'text-dashboard-accent' : 'text-dashboard-faint'}`}
        >
          {icon}
        </span>
      )}
      <span className="flex items-center gap-1.5 flex-1 min-w-0">
        <span className="truncate">{label}</span>
        {badge && (
          <span className="px-1.5 py-[1px] rounded-full bg-dashboard-positive-bg text-dashboard-positive-fg text-[10px] font-semibold leading-[14px] tracking-[0.01em]">
            {badge}
          </span>
        )}
      </span>
    </>
  )

  // An external destination is a different app behind the same gateway, so it
  // has to be a real navigation rather than a client-side route change — and it
  // opens in a new tab, so following it does not take the co-owner out of the
  // dashboard they were working in.
  //
  // `noopener` matters as much as the target: without it the opened page gets a
  // handle on this one through `window.opener` and can navigate it.
  if (external) {
    return (
      <a
        href={href}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {content}
        {/* Announced by a screen reader, which otherwise gets no warning that
            focus is about to move to a new tab. */}
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    )
  }

  return (
    <Link href={href} onClick={onClick} className={className} aria-current={active ? 'page' : undefined}>
      {content}
    </Link>
  )
}
